"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRightIcon, CheckIcon } from "@radix-ui/react-icons";
import { cn } from "@/lib/utils";
import { Zap, Image as ImageIcon } from "lucide-react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export function Pricing() {
  const t = useTranslations("pricing");
  const [isYearly, setIsYearly] = useState(false);
  const [isPremiumDialogOpen, setIsPremiumDialogOpen] = useState(false);
  const router = useRouter();

  const buttonStyles = {
    default: cn(
      "h-12 bg-white/20 rounded-full",
      "hover:bg-white/30",
      "text-white",
      "border border-white/10",
      "hover:border-white/20",
      "shadow-sm hover:shadow-md",
      "text-sm font-medium"
    ),
    highlight: cn(
      "h-12 bg-gradient-to-r from-red-800 to-red-900 rounded-full",
      "hover:bg-gradient-to-r hover:from-red-700 hover:to-red-800",
      "text-white",
      "shadow-[0_1px_15px_rgba(185,28,28,0.3)]",
      "hover:shadow-[0_1px_20px_rgba(185,28,28,0.4)]",
      "font-semibold text-base"
    ),
  };

  const badgeStyles = cn(
    "px-4 py-1.5 text-sm font-medium rounded-full",
    "bg-gradient-to-r from-red-800 to-red-900",
    "text-white",
    "border-none shadow-lg"
  );

  const tiers = [
    {
      name: t("tiers.basic.name"),
      price: {
        monthly: t("tiers.basic.price.monthly"),
        yearly: t("tiers.basic.price.yearly"),
      },
      description: t("tiers.basic.description"),
      icon: (
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-red-800/30 to-red-900/30 blur-2xl rounded-full" />
          <ImageIcon className="w-7 h-7 relative z-10 text-red-400" />
        </div>
      ),
      features: [
        {
          name: t("tiers.basic.features.0.name"),
          description: t("tiers.basic.features.0.description"),
          included: true,
        },
        {
          name: t("tiers.basic.features.1.name"),
          description: t("tiers.basic.features.1.description"),
          included: true,
        },
        {
          name: t("tiers.basic.features.2.name"),
          description: t("tiers.basic.features.2.description"),
          included: true,
        },
        {
          name: t("tiers.basic.features.3.name"),
          description: t("tiers.basic.features.3.description"),
          included: false,
        },
      ],
      button: t("tiers.basic.button"),
    },
    {
      name: t("tiers.premium.name"),
      price: {
        monthly: t("tiers.premium.price.monthly"),
        yearly: t("tiers.premium.price.yearly"),
      },
      description: t("tiers.premium.description"),
      highlight: true,
      badge: t("tiers.premium.badge"),
      icon: (
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-red-800/30 to-red-900/30 blur-2xl rounded-full" />
          <Zap className="w-7 h-7 relative z-10 text-red-400" />
        </div>
      ),
      features: [
        {
          name: t("tiers.premium.features.0.name"),
          description: t("tiers.premium.features.0.description"),
          included: true,
        },
        {
          name: t("tiers.premium.features.1.name"),
          description: t("tiers.premium.features.1.description"),
          included: true,
        },
        {
          name: t("tiers.premium.features.2.name"),
          description: t("tiers.premium.features.2.description"),
          included: true,
        },
        {
          name: t("tiers.premium.features.3.name"),
          description: t("tiers.premium.features.3.description"),
          included: true,
        },
      ],
      button: t("tiers.premium.button"),
    },
  ];

  return (
    <section
      id="pricing"
      className={cn(
        "relative bg-black text-white",
        "pb-24 pt-12 px-4",
        "overflow-hidden"
      )}
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-1/4 w-64 h-64 bg-gradient-to-r from-red-800/20 to-red-900/20 rounded-full blur-3xl opacity-50" />
        <div className="absolute bottom-20 right-1/4 w-80 h-80 bg-gradient-to-r from-red-800/20 to-red-900/20 rounded-full blur-3xl opacity-50" />
      </div>

      <div className="w-full max-w-5xl mx-auto relative">
        <div className="flex flex-col items-start md:items-center gap-2 lg:gap-4 mb-8 lg:mb-12">
          <h2 className="text-2xl lg:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-neutral-100 via-neutral-300 to-neutral-500 text-left md:text-center w-full">
            {t("title")}
          </h2>
          <p className="text-white/70 text-left md:text-center max-w-xl w-full">
            {t("subtitle")}
          </p>
          <div className="flex w-full md:w-fit md:inline-flex items-center p-1.5 bg-white/5 backdrop-blur-md rounded-full border border-white/10 shadow-sm mt-4">
            {[t("periods.monthly"), t("periods.yearly")].map((period) => (
              <button
                key={period}
                onClick={() => setIsYearly(period === t("periods.yearly"))}
                className={cn(
                  "flex-1 md:flex-initial px-8 py-2.5 text-sm font-medium rounded-full transition-all duration-300",
                  (period === t("periods.yearly")) === isYearly
                    ? "bg-white/20 text-white shadow-lg"
                    : "text-white/60 hover:text-white"
                )}
              >
                {period}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {tiers.map((tier) => (
            <div
              key={tier.name}
              className={cn(
                "relative group backdrop-blur-md",
                "rounded-3xl transition-all duration-300",
                "flex flex-col",
                tier.highlight
                  ? "bg-gradient-to-b from-white/10 to-transparent border-white/10"
                  : "bg-white/5 border-white/5",
                "border",
                tier.highlight ? "shadow-xl" : "shadow-md",
                "hover:translate-y-0 hover:shadow-lg"
              )}
            >
              {tier.badge && tier.highlight && (
                <div className="absolute -top-4 left-6">
                  <Badge className={badgeStyles}>{tier.badge}</Badge>
                </div>
              )}

              <div className="p-8 flex-1">
                <div className="flex items-center justify-between mb-6">
                  <div
                    className={cn(
                      "p-3 rounded-full",
                      tier.highlight
                        ? "bg-white/10 text-white"
                        : "bg-white/10 text-white/80"
                    )}
                  >
                    {tier.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-white">
                    {tier.name}
                  </h3>
                </div>

                <div className="mb-6">
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-white">
                      ${isYearly ? tier.price.yearly : tier.price.monthly}
                    </span>
                    <span className="text-sm text-white/60">
                      /{isYearly ? "year" : "month"}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-white/70">
                    {tier.description}
                  </p>
                </div>

                <div className="space-y-4">
                  {tier.features.map((feature) => (
                    <div key={feature.name} className="flex gap-4">
                      <div
                        className={cn(
                          "mt-1 p-0.5 rounded-full transition-colors duration-200",
                          feature.included
                            ? "text-emerald-400"
                            : "text-white/30"
                        )}
                      >
                        <CheckIcon className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-white">
                          {feature.name}
                        </div>
                        <div className="text-sm text-white/60">
                          {feature.description}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-8 pt-0 mt-auto">
                <Button
                  className={cn(
                    "w-full relative transition-all duration-300",
                    tier.highlight
                      ? buttonStyles.highlight
                      : buttonStyles.default
                  )}
                  onClick={() => {
                    if (tier.highlight) {
                      setIsPremiumDialogOpen(true);
                    } else {
                      router.push("/try");
                    }
                  }}
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    {tier.button}
                    <ArrowRightIcon className="w-4 h-4" />
                  </span>
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={isPremiumDialogOpen} onOpenChange={setIsPremiumDialogOpen}>
        <DialogContent className={cn(
            "rounded-2xl"
        )}>
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-white">{t("dialog.title")}</DialogTitle>
            <DialogDescription className="text-white/70">
              {t("dialog.description")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsPremiumDialogOpen(false)}
              className="mt-4"
            >
              {t("dialog.dismiss")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
